import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import DateObject from "react-date-object";
import persian from "react-date-object/calendars/persian.js";
import persian_fa from "react-date-object/locales/persian_fa.js";
import { dayToWeek } from "../../calendar/Calendarr";
import { notifyUnlocked } from "../../../components/achievements/AchievementUnlockToast";
import { toFarsiNumber } from "../../../custom/helpers";
import { useGroupApi, toIsoDay } from "../groupApi";
import { Sheet } from "./ui";

const MONTHS = persian_fa.months.map((m) => m[0]);

const jalali = (iso) =>
  new DateObject({ date: new Date(`${iso}T12:00:00`), calendar: persian, locale: persian_fa });

// Jalali day / month / year selects that read and write a Gregorian ISO date.
function JalaliDateSelect({ value, onChange, label }) {
  const d = jalali(value);
  const year = d.year;
  const month = d.month.number;
  const day = d.day;
  const length = new DateObject({ calendar: persian, locale: persian_fa, year, month, day: 1 }).month.length;
  const thisYear = jalali(new Date().toISOString().slice(0, 10)).year;
  const years = [];
  for (let y = thisYear - 1; y <= thisYear + 2; y++) years.push(y);

  const emit = (y, m, dd) => {
    const len = new DateObject({ calendar: persian, locale: persian_fa, year: y, month: m, day: 1 }).month.length;
    onChange(toIsoDay(new DateObject({ calendar: persian, locale: persian_fa, year: y, month: m, day: Math.min(dd, len) })));
  };

  return (
    <div className="grpField" style={{ marginBottom: 10 }}>
      <span className="yekan">{label}</span>
      <div className="grpDateRow">
        <select className="grpInput" aria-label={`${label} - روز`} value={day} onChange={(e) => emit(year, month, +e.target.value)}>
          {Array.from({ length }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{toFarsiNumber(n)}</option>)}
        </select>
        <select className="grpInput" aria-label={`${label} - ماه`} value={month} onChange={(e) => emit(year, +e.target.value, day)}>
          {MONTHS.map((name, i) => <option key={name} value={i + 1}>{name}</option>)}
        </select>
        <select className="grpInput" aria-label={`${label} - سال`} value={year} onChange={(e) => emit(+e.target.value, month, day)}>
          {years.map((y) => <option key={y} value={y}>{toFarsiNumber(y)}</option>)}
        </select>
      </div>
    </div>
  );
}

function presetRange(kind) {
  const today = new Date();
  if (kind === "week" || kind === "nextWeek") {
    const base = new Date(today);
    if (kind === "nextWeek") base.setDate(base.getDate() + 7);
    const w = dayToWeek(base);
    return [toIsoDay(w[0]), toIsoDay(w[6])];
  }
  const t = new DateObject({ date: today, calendar: persian, locale: persian_fa });
  const first = new DateObject({ calendar: persian, locale: persian_fa, year: t.year, month: t.month.number, day: 1 });
  const last = new DateObject({ calendar: persian, locale: persian_fa, year: t.year, month: t.month.number, day: t.month.length });
  return [toIsoDay(first), toIsoDay(last)];
}

export default function CopyCalendarSheet({ groupId, onClose }) {
  const { json } = useGroupApi();
  const [mode, setMode] = useState("all");            // 'all' | 'range'
  const [range, setRange] = useState(() => presetRange("week"));
  const [resolution, setResolution] = useState("skip");
  const [summary, setSummary] = useState(null);       // null = loading
  const [busy, setBusy] = useState(false);

  const body = useMemo(
    () => (mode === "all"
      ? { conflict_resolution: resolution }
      : { start_date: range[0], end_date: range[1], conflict_resolution: resolution }),
    [mode, range, resolution]
  );
  const invalidRange = mode === "range" && range[1] < range[0];

  // live preview of what the copy would do (nothing is written)
  useEffect(() => {
    if (invalidRange) { setSummary(null); return; }
    let ignore = false;
    setSummary(null);
    const t = setTimeout(async () => {
      try {
        const r = await json(`groups/${groupId}/calendar/copy-to-personal`, "POST", { ...body, dry_run: true });
        if (!ignore) setSummary(r);
      } catch (e) {
        if (!ignore) setSummary({ error: e.message });
      }
    }, 250);
    return () => { ignore = true; clearTimeout(t); };
  }, [body, groupId, json, invalidRange]);

  const nothingToDo = summary && !summary.error && summary.copied + summary.replaced === 0;

  const submit = async () => {
    setBusy(true);
    try {
      const r = await json(`groups/${groupId}/calendar/copy-to-personal`, "POST", body);
      const n = r.copied + r.replaced;
      toast.success(`${toFarsiNumber(n)} وعده به تقویم تو اضافه شد`);
      if (r.newly_unlocked && r.newly_unlocked.length > 0) notifyUnlocked(r.newly_unlocked);
      onClose();
    } catch (e) {
      toast.error(e.message);
      setBusy(false);
    }
  };

  const Chip = ({ active, onClick, children }) => (
    <button type="button" className={`grpChip ${active ? "grpChip-active" : ""}`} onClick={onClick}>{children}</button>
  );

  return (
    <Sheet title="کپی تقویم گروه به تقویم من" onClose={() => !busy && onClose()}>
      <div className="grpSectionTitle yekan">چی کپی بشه؟</div>
      <div className="grpChoice">
        <Chip active={mode === "all"} onClick={() => setMode("all")}>کل تقویم گروه</Chip>
        <Chip active={mode === "range"} onClick={() => setMode("range")}>یه بازه مشخص</Chip>
      </div>

      {mode === "range" && (
        <>
          <div className="grpChoice">
            <Chip onClick={() => setRange(presetRange("week"))}>این هفته</Chip>
            <Chip onClick={() => setRange(presetRange("nextWeek"))}>هفته بعد</Chip>
            <Chip onClick={() => setRange(presetRange("month"))}>این ماه</Chip>
          </div>
          <JalaliDateSelect label="از تاریخ" value={range[0]} onChange={(v) => setRange([v, range[1]])} />
          <JalaliDateSelect label="تا تاریخ" value={range[1]} onChange={(v) => setRange([range[0], v])} />
          {invalidRange && <div className="grpNotice">تاریخ پایان باید بعد از تاریخ شروع باشه</div>}
        </>
      )}

      <div className="grpSectionTitle yekan" style={{ marginTop: 6 }}>اگه تو تقویم من برای اون وعده غذا گذاشته بودم؟</div>
      <div className="grpChoice">
        <Chip active={resolution === "skip"} onClick={() => setResolution("skip")}>نگهش دار</Chip>
        <Chip active={resolution === "overwrite"} onClick={() => setResolution("overwrite")}>جایگزینش کن</Chip>
      </div>

      <div className="grpCopySummary yekan" aria-live="polite">
        {invalidRange ? null : summary === null ? (
          <div className="grpSkeleton" style={{ height: 52 }} />
        ) : summary.error ? (
          summary.error
        ) : summary.total === 0 ? (
          "تو این بازه وعده‌ای تو تقویم گروه نیست."
        ) : (
          <>
            <div><b>{toFarsiNumber(summary.copied + summary.replaced)}</b> وعده از {toFarsiNumber(summary.total)} وعده به تقویم تو اضافه می‌شه</div>
            {summary.replaced > 0 && <div className="caption-1 grey-2">{toFarsiNumber(summary.replaced)} وعده جایگزین وعده‌های فعلی تو می‌شه</div>}
            {summary.kept_existing > 0 && <div className="caption-1 grey-2">{toFarsiNumber(summary.kept_existing)} وعده چون از قبل غذای دیگه‌ای تو تقویمت داری رد می‌شه</div>}
            {summary.already_there > 0 && <div className="caption-1 grey-2">{toFarsiNumber(summary.already_there)} وعده از قبل تو تقویمت هست</div>}
          </>
        )}
      </div>

      <button type="button" className="grpBtn grpBtn-block" disabled={busy || invalidRange || summary === null || !!(summary && summary.error) || nothingToDo} onClick={submit}>
        {busy ? "..." : "کپی به تقویم من"}
      </button>
      <p className="caption-1 grey-2 yekan" style={{ textAlign: "center", margin: "10px 0 0" }}>
        این فقط یه کپیه؛ بعدش تقویم گروه و تقویم تو مستقل از هم هستن. <Link to="/calendar" className="purple-1" onClick={onClose}>دیدن تقویم من</Link>
      </p>
    </Sheet>
  );
}
